/**
 * クライアント側の認証ガードが「認証不要」とみなすページの単一定義。
 *
 * 公開ページの正本は `lib/middleware/route-registry.ts`（middleware が使う ALL_PUBLIC_PAGE_PATHS）。
 * ここはその正本を **必ず包含** したうえで、クライアント固有の除外（/api や /print 等、
 * middleware の page 定義には載らないもの）だけを足す。
 *
 * 2箇所に同じ意味の配列を持つと必ず割れる。実際 2026-08-07 に
 * `/building-application/apply`（お客様が入力する建築申込フォーム）と `/customer-mypage` が
 * 正本にはあるのにクライアント側に無く、サーバが 200 を返すのに画面だけ /login へ
 * 弾かれていた（お客様には社内ログイン画面が出る）。
 * 🔴 訂正（2026-09-14）: `/customer-mypage` はお客様のページではなく、スタッフ用のお客様マイページ一覧。
 * 正本の名簿から外した（app/customer-mypage/route-config.ts）ので、ここでも公開扱いにならない。
 *
 * @canonical 公開ページ判定は ALL_PUBLIC_PAGE_PATHS が正本。本ファイルは派生。
 */
import { ALL_PUBLIC_PAGE_PATHS } from '@/lib/middleware/route-registry'

/**
 * middleware の「公開ページ」定義には載らないが、クライアントの
 * リダイレクトガードでは素通しする必要があるパス。
 * - `/api`: そもそもページ遷移ではない
 * - `/print`: 印刷用ビュー
 * - 各 intake / ICpochipochi / LINE 等: お客様が未認証で開く画面
 */
export const CLIENT_ONLY_PUBLIC_PATHS = [
  '/login',
  '/api',
  '/reset-password',
  '/print',
  '/intake-v3',
  '/intake-v4',
  '/intake-v5',
  '/hearing',
  '/ICpochipochi/portal',
  '/customer-portal',
  '/customer',
  '/ICpochipochi/catalog',
  '/ICpochipochi/admin',
  '/invite',
  '/open',
  '/g-plans/check',
  // 2段階認証の6桁の画面。パスワードが通った時点ではセッションがまだ無い（better-auth が消して
  // two_factor cookie を置く）。通すかどうかは middleware が cookie で決める（2026-10-16 報告）
  '/two-factor',
] as const

/** 正本 ∪ クライアント固有。重複は除く。 */
export const CLIENT_PUBLIC_PATHS: readonly string[] = Array.from(
  new Set<string>([...ALL_PUBLIC_PAGE_PATHS, ...CLIENT_ONLY_PUBLIC_PATHS])
)

/**
 * middleware と同じ前方一致規則で公開判定する。
 * `/building-application` は非公開のまま、`/building-application/apply` 配下だけ公開になる。
 */
export function isClientPublicPath(pathname: string): boolean {
  return CLIENT_PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + '/'))
}
